// `DeleteAccountDialog` — type-email-to-confirm destructive flow (US-014).
//
// Per the prototype's "Zone sensible" block (proto line 991-995): the
// CTA opens a modal that demands the user re-type their full email
// address + (when present) the current password. Better Auth wires
// `/auth/delete-user` to require the password for credential accounts.
//
// We never bypass the email match — even if the password is correct,
// the destructive action is gated on a verbatim email confirmation.

import { useState } from 'react';

import { ApiError, useDeleteAccount } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
} from '@barel/ui';
import { useNavigate } from '@tanstack/react-router';

export interface DeleteAccountDialogProps {
  /** Verified email of the current user — required for the confirmation gate. */
  email: string;
}

export function DeleteAccountDialog({ email }: DeleteAccountDialogProps) {
  const t = useT('settings');
  const tCommon = useT('common');
  const navigate = useNavigate();
  const deleteAccount = useDeleteAccount();
  const [open, setOpen] = useState<boolean>(false);
  const [confirmEmail, setConfirmEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setOpen(false);
    setConfirmEmail('');
    setPassword('');
    setError(null);
  };

  const emailMatches = confirmEmail.trim().toLowerCase() === email.toLowerCase();

  const handleDelete = async (): Promise<void> => {
    setError(null);
    if (!emailMatches) {
      setError(t('security.deleteEmailMismatch'));
      return;
    }
    try {
      await deleteAccount.mutateAsync({ password });
      // Server already cleared the cookie + DO record; bounce to login.
      void navigate({ to: '/auth/login', replace: true });
    } catch (err) {
      setError(
        ApiError.is(err) && err.code === 'auth.bad_credentials'
          ? t('security.passwordError')
          : t('security.deleteError'),
      );
    }
  };

  return (
    <div data-component="DeleteAccountDialog">
      <Button
        type="button"
        variant="danger"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="settings-delete-trigger"
      >
        {t('security.deleteAccount')}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next: boolean) => {
          if (!next) close();
        }}
      >
        <DialogContent aria-label={t('security.deleteConfirmTitle')}>
          <DialogHeader
            title={t('security.deleteConfirmTitle')}
            description={t('security.deleteConfirmDescription', { email })}
          />
          <DialogBody>
            {error ? (
              <Banner tone="error" className="mb-3">
                {error}
              </Banner>
            ) : null}
            <div className="flex flex-col gap-3">
              <Field required invalid={confirmEmail.length > 0 && !emailMatches}>
                <Field.Label>{t('security.deleteConfirmEmailLabel')}</Field.Label>
                <Field.Control>
                  <Input
                    type="email"
                    autoComplete="email"
                    value={confirmEmail}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setConfirmEmail(event.target.value)
                    }
                    data-testid="settings-delete-email"
                  />
                </Field.Control>
                {confirmEmail.length > 0 && !emailMatches ? (
                  <Field.Error>{t('security.deleteEmailMismatch')}</Field.Error>
                ) : null}
              </Field>
              <Field>
                <Field.Label>{t('security.deleteConfirmPasswordLabel')}</Field.Label>
                <Field.Control>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setPassword(event.target.value)
                    }
                    data-testid="settings-delete-password"
                  />
                </Field.Control>
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              {tCommon('actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={deleteAccount.isPending}
              disabled={!emailMatches}
              onClick={() => {
                void handleDelete();
              }}
              data-testid="settings-delete-confirm"
            >
              {t('security.deleteConfirmCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
